import { useEffect, useState } from "react";
import { Box, Button, Field, HStack, Input, Textarea, VStack } from "@chakra-ui/react";
import { HiOutlineBuildingOffice2, HiOutlineDocumentText, HiOutlineUser } from "react-icons/hi2";
import ClientService from "@/services/ClientService";
import { showToast } from "@/components/ui/toaster";
import { InfoField, SectionCard } from "@/components/ui/detail/primitives";
import ClientMembersSection from "@/pages/clients/components/ClientMembersSection";
import ClientModelsSection from "@/pages/clients/components/ClientModelsSection";

const PROFILE_FIELDS = [
    "firstName", "lastName", "phoneNumber", "additionalInfo",
    "companyName", "companyNip", "companyWebsite",
    "billingStreet", "billingBuildingNumber", "billingPostalCode", "billingCity", "billingCountry",
];

const toForm = (client) => PROFILE_FIELDS.reduce((acc, key) => ({ ...acc, [key]: client?.[key] ?? "" }), {});

const SECTIONS = [
    {
        id: "owner",
        title: "Dane właściciela",
        icon: <HiOutlineUser />,
        fields: [
            { key: "firstName", label: "Imię", required: true },
            { key: "lastName", label: "Nazwisko", required: true },
            { key: "phoneNumber", label: "Telefon" },
            { key: "additionalInfo", label: "Dodatkowe informacje", textarea: true, full: true },
        ],
    },
    {
        id: "company",
        title: "Dane firmy",
        icon: <HiOutlineBuildingOffice2 />,
        fields: [
            { key: "companyName", label: "Nazwa firmy", full: true },
            { key: "companyNip", label: "NIP" },
            { key: "companyWebsite", label: "Strona WWW" },
        ],
    },
    {
        id: "billing",
        title: "Adres rozliczeniowy",
        icon: <HiOutlineDocumentText />,
        fields: [
            { key: "billingStreet", label: "Ulica" },
            { key: "billingBuildingNumber", label: "Nr budynku" },
            { key: "billingPostalCode", label: "Kod pocztowy" },
            { key: "billingCity", label: "Miasto" },
            { key: "billingCountry", label: "Kraj" },
        ],
    },
];

const ClientDataTab = ({ client, scenarios, onClientChanged }) => {
    const [form, setForm] = useState(() => toForm(client));
    const [editingSection, setEditingSection] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => { setForm(toForm(client)); }, [client]);

    const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

    const cancelEdit = () => {
        setForm(toForm(client));
        setEditingSection(null);
    };

    const save = async () => {
        if (!form.firstName.trim() || !form.lastName.trim()) {
            showToast("Błąd", "Imię i nazwisko są wymagane", "error");
            return;
        }
        setIsSaving(true);
        try {
            await ClientService.updateCompany(client.id, { ...form, isActive: client.isActive });
            showToast("Zapisano", "Dane klienta zaktualizowane", "success");
            setEditingSection(null);
            onClientChanged();
        } catch (err) {
            showToast("Błąd", err?.response?.data ?? "Nie udało się zapisać danych", "error");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <VStack w="full" align="flex-start" gap={6} pt={4}>
            {SECTIONS.map((section) => {
                const isEditing = editingSection === section.id;
                return (
                    <SectionCard
                        key={section.id}
                        icon={section.icon}
                        title={section.title}
                        action={
                            isEditing ? (
                                <HStack gap={2}>
                                    <Button variant="outlined" size="xs" onClick={cancelEdit} disabled={isSaving}>Anuluj</Button>
                                    <Button variant="primary" size="xs" onClick={save} loading={isSaving}>Zapisz</Button>
                                </HStack>
                            ) : (
                                <Button
                                    variant="outlined"
                                    size="xs"
                                    onClick={() => setEditingSection(section.id)}
                                    disabled={editingSection !== null}
                                >
                                    Edytuj
                                </Button>
                            )
                        }
                    >
                        <Box display="grid" gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
                            {section.fields.map((f) => (
                                <Box key={f.key} gridColumn={f.full ? { md: "1 / -1" } : undefined}>
                                    {isEditing ? (
                                        <Field.Root required={f.required}>
                                            <Field.Label color="white" fontSize={13}>
                                                {f.label} {f.required && <Field.RequiredIndicator />}
                                            </Field.Label>
                                            {f.textarea ? (
                                                <Textarea variant="primary" value={form[f.key]} onChange={set(f.key)} rows={3} />
                                            ) : (
                                                <Input variant="primary" value={form[f.key]} onChange={set(f.key)} />
                                            )}
                                        </Field.Root>
                                    ) : (
                                        <InfoField label={f.label}>{client[f.key] || "—"}</InfoField>
                                    )}
                                </Box>
                            ))}
                            {section.id === "owner" && !isEditing && (
                                <InfoField label="E-mail">{client.email}</InfoField>
                            )}
                        </Box>
                    </SectionCard>
                );
            })}

            <ClientMembersSection client={client} onMembersChanged={onClientChanged} />

            <ClientModelsSection scenarios={scenarios} />
        </VStack>
    );
};

export default ClientDataTab;
